<script setup>
/**
 * AddNodeButton.vue
 *
 * The round "+" button shown just outside a flow node's right edge on hover, used
 * to append the next node. Shared by every node with an outgoing branch: the node
 * decides when it renders (`v-if="!hasOutgoingEdge"`) and re-emits `add` as its
 * own `add-node` with its id.
 *
 * Position and the hover reveal live on a plain wrapper div, not on the Button:
 * PrimeVue's unlayered `.p-button { position: relative; transition: … }` beats
 * Tailwind's layered `absolute` / `transition-opacity`. The wrapper relies on the
 * node root carrying `group relative`. The Button itself only handles the look,
 * through scoped `dt` tokens (no `!` overrides).
 *
 * @example
 * <AddNodeButton v-if="!hasOutgoingEdge" accent="orange" @add="emit('add-node', nodeId)" />
 * <AddNodeButton v-if="!hasOutgoingEdge" placement="success" @add="emit('add-node', nodeId)" />
 *
 * @prop {String} accent    - Hover hue, an Aura palette name: 'cyan' (default) |
 *                            'orange' | 'violet' | 'green' | 'purple'.
 * @prop {String} placement - 'center' (default): vertically centred beside the node.
 *                            'success': level with the success handle (35% height),
 *                            past its label (ApiCall, AskAgent).
 *
 * @emits add - The button was clicked. The click does not propagate to the node.
 */

import { computed } from 'vue';

const props = defineProps({
  /** Hover hue, an Aura palette name. */
  accent: {
    type: String,
    default: 'cyan',
    validator: (v) => ['cyan', 'orange', 'violet', 'green', 'purple'].includes(v)
  },
  /** 'center' (most nodes) or 'success' (beside the success handle at 35%). */
  placement: {
    type: String,
    default: 'center',
    validator: (v) => ['center', 'success'].includes(v)
  }
});

const emit = defineEmits(['add']);

/**
 * Scoped design tokens: a 24px circle, gray-600 fill + gray-400 ring at rest,
 * {hue}.600 fill + {hue}.400 ring on hover, {hue}.700 when pressed, white icon.
 * `rounded` + an icon-only button lets PrimeVue's own
 * `.p-button-icon-only.p-button-rounded` rule set width = height = iconOnlyWidth
 * and a 50% radius. A computed keeps the object identity stable across
 * re-renders, so the shallow `dt` watcher only re-injects styles if `accent`
 * itself changes.
 */
const accentDt = computed(() => {
  const hue = props.accent;
  return {
    root: { iconOnlyWidth: '1.5rem', paddingY: '0' },
    colorScheme: {
      dark: {
        root: {
          primary: {
            background: '{gray.600}',
            borderColor: '{gray.400}',
            color: '#ffffff',
            hoverBackground: `{${hue}.600}`,
            hoverBorderColor: `{${hue}.400}`,
            hoverColor: '#ffffff',
            activeBackground: `{${hue}.700}`,
            activeBorderColor: `{${hue}.400}`,
            activeColor: '#ffffff'
          }
        }
      }
    }
  };
});

/** 2px ring: @primeuix/themes has no button border-width token. */
const BUTTON_PT = { root: { style: 'border-width: 2px' } };
</script>

<template>
  <!-- Position + reveal on a plain wrapper (see header). `flex` keeps the wrapper
       exactly the Button's 24x24 so -translate-y-1/2 centres it as before. -->
  <div
    :class="[
      'absolute -translate-y-1/2 z-10 flex nodrag opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity',
      placement === 'success' ? '-right-32 top-[35%]' : '-right-9 top-1/2'
    ]"
  >
    <Button
      rounded
      :dt="accentDt"
      :pt="BUTTON_PT"
      :aria-label="$t('flowEditor.nodes.common.addNodeTooltip')"
      v-tooltip.top="$t('flowEditor.nodes.common.addNodeTooltip')"
      @click.stop="emit('add')"
    >
      <!-- #icon (not the default slot) so PrimeVue treats it as icon-only. -->
      <template #icon>
        <span class="material-icons" style="font-size: 1rem" aria-hidden="true">add</span>
      </template>
    </Button>
  </div>
</template>
